import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Tag, Plus, X, ChevronDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXPERIENCE_LEVELS = [
  { value: 'beginner', label: 'Beginner (0-2 yrs)' },
  { value: 'intermediate', label: 'Intermediate (3-7 yrs)' },
  { value: 'expert', label: 'Expert (8+ yrs)' },
];

const PRICING_PRESETS = [
  'R200 - R500',
  'R500 - R1,500',
  'R1,500 - R5,000',
  'R5,000 - R15,000',
  'R15,000+',
  'Custom',
];

const emptyService = (service_id = '') => ({
  service_id,
  experience_level: 'intermediate',
  years_experience: 3,
  pricing_range: 'R500 - R1,500',
});

/** Step 4 of 5 — Services & Specialties. Add one or more services with
 *  per-service experience level, years, and pricing range. */
const StepServices = ({ services, onChange }) => {
  const [catalog, setCatalog] = useState([]);
  const [search, setSearch] = useState('');
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    axios.get(`${API}/services/popular`).then((res) => setCatalog(res.data)).catch(() => {});
  }, []);

  const getName = (slug) => catalog.find((s) => s.slug === slug)?.name || slug;
  const selectedSlugs = services.map((s) => s.service_id);
  const filtered = catalog
    .filter((s) => !selectedSlugs.includes(s.slug))
    .filter((s) => !search.trim() || s.name.toLowerCase().includes(search.toLowerCase()));

  const addService = (svc) => {
    onChange([...services, emptyService(svc.slug)]);
    setSearch('');
    setShowPicker(false);
  };

  const updateService = (slug, field, value) => {
    onChange(services.map((s) =>
      s.service_id === slug ? { ...s, [field]: field === 'years_experience' ? (parseInt(value, 10) || 0) : value } : s
    ));
  };

  const removeService = (slug) => onChange(services.filter((s) => s.service_id !== slug));

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
        <Tag size={16} /> Services Offered *
      </label>

      <div className="space-y-3 mb-3">
        {services.length === 0 && (
          <div className="text-sm text-gray-500 text-center py-6 border border-dashed border-gray-200 rounded-lg">
            No services added yet. Use the picker below to add at least one.
          </div>
        )}
        {services.map((svc) => (
          <div key={svc.service_id} data-testid={`service-card-${svc.service_id}`} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-900">{getName(svc.service_id)}</h3>
              <button type="button" onClick={() => removeService(svc.service_id)} data-testid={`service-remove-${svc.service_id}`} className="text-gray-400 hover:text-red-500 transition-colors">
                <X size={16} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Experience level</label>
                <select
                  value={svc.experience_level}
                  onChange={(e) => updateService(svc.service_id, 'experience_level', e.target.value)}
                  data-testid={`service-exp-level-${svc.service_id}`}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
                >
                  {EXPERIENCE_LEVELS.map((lvl) => <option key={lvl.value} value={lvl.value}>{lvl.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Years of experience</label>
                <input
                  type="number" min="0" max="60" value={svc.years_experience}
                  onChange={(e) => updateService(svc.service_id, 'years_experience', e.target.value)}
                  data-testid={`service-years-${svc.service_id}`}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Pricing range</label>
                <select
                  value={PRICING_PRESETS.includes(svc.pricing_range) ? svc.pricing_range : 'Custom'}
                  onChange={(e) => { const v = e.target.value; updateService(svc.service_id, 'pricing_range', v === 'Custom' ? '' : v); }}
                  data-testid={`service-pricing-select-${svc.service_id}`}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
                >
                  {PRICING_PRESETS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                {(!PRICING_PRESETS.includes(svc.pricing_range) || svc.pricing_range === 'Custom') && (
                  <input
                    type="text" value={svc.pricing_range}
                    onChange={(e) => updateService(svc.service_id, 'pricing_range', e.target.value)}
                    data-testid={`service-pricing-custom-${svc.service_id}`}
                    placeholder="e.g., R800 - R2,500"
                    className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="relative">
        <button
          type="button" onClick={() => setShowPicker((v) => !v)} data-testid="open-service-picker-btn"
          className="w-full flex items-center justify-between px-4 py-2.5 border-2 border-dashed border-gray-300 hover:border-[#37b34a] hover:bg-[#37b34a]/5 rounded-lg text-sm font-medium text-gray-600 hover:text-[#37b34a] transition-colors"
        >
          <span className="flex items-center gap-2"><Plus size={16} /> Add a service</span>
          <ChevronDown size={16} />
        </button>
        {showPicker && (
          <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-72 overflow-hidden">
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search services..." data-testid="service-search-input" autoFocus
              className="w-full px-4 py-2.5 text-sm border-b border-gray-200 outline-none"
            />
            <div className="max-h-56 overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="px-4 py-3 text-sm text-gray-400">No services available</p>
              ) : (
                filtered.slice(0, 30).map((svc) => (
                  <button
                    key={svc.id} type="button" onClick={() => addService(svc)}
                    data-testid={`service-option-${svc.slug}`}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors flex items-center justify-between"
                  >
                    <span className="text-gray-700">{svc.name}</span>
                    <span className="text-xs text-gray-400">{svc.category}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
      <p className="text-xs text-gray-500 mt-2">{services.length} service(s) added</p>
    </div>
  );
};

export default StepServices;
